import { isNumber } from 'constants/regexExpressions';
import { imagesComingSoonIconSvg } from 'constants/svg-assets';
import createPictureImageSources from 'utils/createPictureImageSources';
import MarketingStatus from 'enums/marketingStatus';
import { ratingStarSvgHtml } from '../constants/svg-icons';

export default class PaidCommunitySummaryCard {

  /**
   * Maps raw card data into this instance.
   *
   * This is the single place where card properties and derived values
   * are assigned. It centralizes all mappings to keep this legacy flow
   * predictable and maintainable.
   *
   * IMPORTANT:
   * - Mapping and derived values only.
   * - No business logic, side effects, callbacks, or async behavior.
   *
   * @param {Object} card - Raw card data.
   */
  setInformation(card) {
    Object.assign(this, card);

    // Derived values
    this.domId = `mapSummaryCard${this.id}`;
    this.isPriceReduction = !!this.priceReduced;
    this.isSitePlan = Boolean(this.sitePlanCenterLatitude && this.sitePlanCenterLongitude);
    this.errorImage = imagesComingSoonIconSvg;

    const { sources, src } = createPictureImageSources({
      url: this.thumbnail || this.errorImage,
      breakpoints: {
        '2x': { width: 286, height: 208 },
        '3x': { width: 350, height: 312 }
      },
      irsParameters: { mode: 'crop', progressive: true }
    });

    this.sources = sources;
    this.src = src;
  }

  /**
   * Returns the tracking label based on the marketing status.
   * @returns {string}
   */
  getTrackingListingLabel() {
    if (this.marketingStatus === MarketingStatus.ComingSoon.toString()) {
      return 'CommunityResults.ComingSoonMapCard.Community';
    }

    return 'CommunityResults.MapCard.Community';
  }

  /**
   * Determines the call-to-action text depending on the card state.
   * @returns {string}
   */
  getCallToAction() {
    if (this.marketingStatus === MarketingStatus.ComingSoon.toString()) {
      return 'Get more information';
    }

    return 'Free brochure';
  }

  /**
   * Returns the opener key used by the lead generator.
   * @returns {string}
   */
  getLeadGeneratorOpener() {
    if (this.marketingStatus === MarketingStatus.ComingSoon.toString()) {
      return 'more-information-paid';
    }

    return 'get-brochure-paid';
  }

  /**
   * Returns the DOM element for this summary card.
   * @returns {HTMLElement|null}
   */
  getElement() {
    return document.getElementById(this.domId);
  }

  /**
   * Returns the formatted price string.
   * @returns {string}
   */
  getPrice() {
    if (this.isLimited) {
      return 'Pricing coming soon';
    }
    
    const containsNumbers = isNumber.test(this.price);

    if (containsNumbers) {
      return `${this.price}`;
    }

    return this.price;
  }

  /**
   * Renders the reduced price badge if applicable.
   * @returns {string}
   */
  getPriceReducedLabel() {
    if (!this.isPriceReduction) {
      return '';
    }

    return `
      <div class="nhs-n1-c-card__badge nhs-n1-c-card__badge--reduced" data-qa="reduced-price">
        Recently reduced
      </div>`;
  }

  /**
   * Builds the list of specifications such as bedrooms, bathrooms, etc.
   * @returns {string}
   */
  getSpecifications() {
    const specs = [];

    if (this.formattedBedrooms) {
      specs.push(`<div><b>${this.formattedBedrooms}</b> Bd</div>`);
    }

    if (this.formattedBathrooms) {
      specs.push(`<div><b>${this.formattedBathrooms}</b> Ba</div>`);
    }

    if (this.formattedGarages) {
      specs.push(`<div><b>${this.formattedGarages}</b> Gr</div>`);
    }

    if (this.formattedSquareFeet) {
      specs.push(`<div><b>${this.formattedSquareFeet}</b> SqFt</div>`);
    }

    if (specs.length === 0) {
      return '';
    }

    return `
      <div class="nhs-c-card--summary__specifications nhs-u-text--ellipsis" data-qa="specifications">
        ${specs.join(' <span>|</span> ')}
      </div>`;
  }

  /**
   * Returns the community type prefix based on the adult-restricted flag.
   * @returns {string}
   */
  getCommunityTypeLabel() {
    return this.isAdult ? '55+ Community in' : 'Community in';
  }

  /**
   * Builds the price section including reduction badge if applicable.
   * @returns {string}
   */
  getPriceTitle() {
    if (this.isPriceReduction) {
      return `
        <h4 class="nhs-c-card--summary__price" data-qa="Map-Price">
          <span class="nhs-u-text--ellipsis">${this.getPrice()}</span>
          ${this.getPriceReducedLabel()}
        </h4>`;
    }

    return `
      <h4 class="nhs-c-card--summary__price" data-qa="Map-Price">
        <span>${this.getPrice()}</span>
      </h4>`;
  }

  /**
   * Builds the <picture> element including responsive sources and fallback image.
   * @returns {string}
   */
  getImage() {
    const sourcesHtml = this.sources
      .map(s => `<source srcset="${s.srcset}" type="${s.format}"></source>`)
      .join('');

    return `
      <picture class="nhs-c-card--summary__image" data-marketId="${this.id}">
        ${sourcesHtml}
        <img src="${this.src}"
          data-qa="map-image" data-summary-img class="nhs-c-card--summary__image"
          onerror="document.dispatchEvent(
              new CustomEvent('image-load-error', {
                bubbles: true,
                detail: { image: this, errorImage: '${this.errorImage}' }
              })
            )"
            onload="this.dataset.load=true">
      </picture>`;
  }

  /**
   * Renders the builder section with optional rating.
   * @returns {string}
   */
  getBuilder() {
    if (+this.builderRating > 0) {
      return `
        <div class="nhs-c-card--summary__builder" aria-label="${this.builderRating} out of 5 stars">
          <span data-qa="card-builder-name" class="nhs-u-text--ellipsis">By ${this.brandName}</span>
          <div class="nhs-n1-c-card__rating" data-qa="card-builder-rating">
            <span data-qa="card-builder-rating-icon">${ratingStarSvgHtml}</span>
            <span>${this.builderRating}</span>
          </div>
        </div>`;
    }

    return `
      <p class="nhs-c-card--summary__builder nhs-u-text--ellipsis">
        <span class="nhs-u-text--ellipsis">By ${this.brandName}</span>
      </p>`;
  }

  /**
   * Returns the button used to open the interactive site plan.
   * @returns {string}
   */
  getInteractiveSitePlanButton() {
    if (!this.isSitePlan) {
      return '';
    }

    return `
      <button
        class="nhs-c-button--text-only nhs-c-button--primary-ui nhs-c-button--small-32"
        data-interactive-plan-button
        data-latitude="${this.sitePlanCenterLatitude}"
        data-longitude="${this.sitePlanCenterLongitude}"
        data-community-id="${this.id}">
        Interactive map
      </button>`;
  }

  /**
   * Returns the main call-to-action button for the card.
   * @returns {string}
   */
  getCallToActionButton() {
    if (this.isSitePlan) {
      return '';
    }

    const callToAction = this.getCallToAction();
    if (!callToAction) {
      return '';
    }

    const opener = this.getLeadGeneratorOpener();

    return `
      <button
        class="nhs-c-button--text-only nhs-c-button--primary-lead nhs-c-button--small-32"
        data-button-action="${callToAction}"
        data-url="${this.url}"
        data-variation="paid"
        data-from-page="${this.getTrackingListingLabel()}"
        data-lead-generator-opener="${opener}">
        ${callToAction}
      </button>`;
  }

  /**
   * Builds all data-* attributes required for the root card element.
   * @returns {string}
   */
  getDataAttributes() {
    const attributes = {
      'data-map-summary-card': true,
      'data-summary-content': true,
      'data-url': this.url,
      'data-latitude': this.latitude,
      'data-longitude': this.longitude,
      'data-id': this.id,
      'data-type': 'community',
      'data-paid-community': true,
      'data-is-basic': false,
      'data-is-paid': true,
      'data-community-id': this.id,
      'data-builder-id': this.builderId,
      'data-brand-name': this.brandName,
      'data-community-name': this.name,
      'data-marketing-status-label': this.marketingStatus,
      'data-image-url': this.thumbnail,
      'data-price-low': this.priceLow,
      'data-price-high': this.priceHigh,
      'data-city': this.city,
      'data-state-abbreviation': this.stateAbbreviation,
      'data-zip': this.zip,
      ...(this.isFeaturedListing ? {
        'data-is-featured-listing': this.isFeaturedListing
      } : {}),
    };

    return Object.entries(attributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
  }

  /**
   * Builds the class attribute for the card.
   * @returns {string}
   */
  getClassNames() {
    const classNames = [
      'nhs-c-card--summary',
    ];

    if (this.isFeaturedListing) {
      classNames.push('nhs-c-card--summary-spotlight');
    }

    return `class="${classNames.join(' ')}"`;
  }

  /**
   * Returns the full HTML of the summary card.
   * @returns {string}
   */
  getHtml() {
    return `
      <div class="nhs-c-tooltip--map nhs-c-tooltip--card" data-map-card>
        <div data-paid-community-summary-card data-card id="${this.domId}" ${this.getDataAttributes()} ${this.getClassNames()}>
          <a href="${this.url}" target="_blank" class="nhs-c-card--summary__wrapper">
            ${this.getImage()}
            <div class="nhs-c-card--summary__body">
              <h3 class="nhs-c-card--summary__title nhs-u-text--ellipsis">${this.name}</h3>
              ${this.getPriceTitle()}
              ${this.getSpecifications()}
              <p class="nhs-c-card--summary__location nhs-u-text--ellipsis">
                ${this.getCommunityTypeLabel()} ${this.city}, ${this.stateAbbreviation} ${this.zip}
              </p>
              ${this.getBuilder()}
            </div>
          </a>
          ${this.getCallToActionButton()}
          ${this.getInteractiveSitePlanButton()}
        </div>
      </div>`;
  }
}
